import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Eye, EyeOff, Moon, Sun, ArrowRight, Sparkles } from "lucide-react";
import api from "@/lib/api";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post("/AdminUsers/login", { email, password });
      if (response.success) {
        login(response.data, response.token);
        toast.success(`Welcome back, ${response.data.username || 'Admin'}!`);
        navigate("/");
      } else {
        toast.error(response.message || "Login failed");
      }
    } catch (err) {
      toast.error(err.message || "An error occurred during login.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-foreground text-background overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-primary/30 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-chart-2/20 blur-3xl" />

        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-primary text-primary-foreground font-display font-bold text-lg">
              P
            </div>
            <span className="font-display font-bold text-lg">Portfolio Admin</span>
          </div>

          <div className="space-y-6 max-w-md">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-background/20 bg-background/5 backdrop-blur">
              <Sparkles className="h-3.5 w-3.5 text-primary" strokeWidth={1.75} />
              <span className="text-xs font-medium">Portfolio Admin v1.0</span>
            </div>
            <h1 className="font-display text-4xl xl:text-5xl font-bold leading-[1.05] tracking-tight">
              Run your portfolio
              <br />
              with the rigor of a
              <br />
              <span className="text-primary">product team.</span>
            </h1>
            <p className="text-background/70 text-base leading-relaxed">
              Portfolio Admin is the operations layer for your personal brand. Edit content, ship blogs, manage testimonials and watch every metric in one calm dashboard.
            </p>
          </div>

          <div className="flex items-center justify-center text-xs text-background/50 font-mono">
            <span>© 2026 Portfolio Admin</span>
          </div>
        </div>
      </div>

      {/* Right login form */}
      <div className="flex-1 flex flex-col justify-center px-6 sm:px-12 lg:px-20 relative">
        <Button
          data-testid="login-theme-toggle"
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          className="absolute top-6 right-6 h-9 w-9"
          aria-label="Toggle theme"
        >
          {theme === "dark" ? <Sun className="h-4 w-4" strokeWidth={1.5} /> : <Moon className="h-4 w-4" strokeWidth={1.5} />}
        </Button>

        <div className="lg:hidden flex items-center gap-2.5 mb-10">
          <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-primary text-primary-foreground font-display font-bold text-lg">
            P
          </div>
          <span className="font-display font-bold text-lg">Portfolio Admin</span>
        </div>

        <div className="max-w-sm w-full mx-auto">
          <div className="space-y-2 mb-8">
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-primary">
              Sign in
            </p>
            <h2 className="font-display text-3xl font-bold tracking-tight">Welcome back</h2>
            <p className="text-sm text-muted-foreground">
              Enter your email and password to log in.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                autoComplete="email"
                className="h-11"
                required
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Password
                </Label>
                <button
                  type="button"
                  data-testid="forgot-password-link"
                  onClick={() => toast.info("Password reset link sent to your email.")}
                  className="text-xs font-medium text-primary hover:underline"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="login-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className="h-11 pr-10"
                  required
                />
                <button
                  type="button"
                  data-testid="toggle-password-visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label="Toggle password visibility"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" strokeWidth={1.5} /> : <Eye className="h-4 w-4" strokeWidth={1.5} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full h-11 group"
            >
              {loading ? "Signing in…" : "Sign in"}
              {!loading && <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={1.75} />}
            </Button>

            <p className="text-center text-xs text-muted-foreground">
              By signing in you agree to Portfolio Admin&apos;s <span className="text-foreground hover:underline cursor-pointer">terms</span> and <span className="text-foreground hover:underline cursor-pointer">privacy</span> policy.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
